import React from 'react';
import LegalDocumentLayout from './LegalDocumentLayout';

export default function PrivacyPage() {
  return (
    <LegalDocumentLayout title="Privacy Policy" effectiveDate="April 2, 2026">
      <h2>1. Introduction</h2>
      <p>
        Sovrynn LLC (&quot;Sovrynn,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your
        privacy. This Privacy Policy describes how we collect, use, disclose, and protect information when you
        use our websites, applications, and related services (the &quot;Services&quot;). By using the Services,
        you agree to this Policy. If you do not agree, please do not use the Services.
      </p>

      <h2>2. Information We Collect</h2>
      <p>We may collect the following categories of information:</p>
      <ul>
        <li>
          <strong>Account and profile data:</strong> name, email address, phone number, password (hashed),
          company or carrier identifiers, and preferences you provide.
        </li>
        <li>
          <strong>Compliance and operational data:</strong> documents you upload, vehicle and driver-related
          information, authority and filing details, scores, alerts, and similar data you choose to store in
          the Services.
        </li>
        <li>
          <strong>Usage and device data:</strong> log data, IP address, browser type, device identifiers, pages
          viewed, and diagnostic information to secure and improve the Services.
        </li>
        <li>
          <strong>Communications:</strong> messages you send to support or through in-product channels.
        </li>
        <li>
          <strong>Payment data:</strong> processed by our payment processors; we generally receive limited
          billing metadata, not full card numbers.
        </li>
      </ul>

      <h2>3. How We Use Information</h2>
      <p>We use information to:</p>
      <ul>
        <li>Provide, operate, maintain, and improve the Services</li>
        <li>Authenticate users, prevent fraud, and protect security</li>
        <li>Send transactional messages, alerts, and service-related notices</li>
        <li>Analyze usage in aggregate to improve product experience</li>
        <li>Comply with legal obligations and enforce our terms</li>
        <li>Provide optional features you enable (such as SMS or email notifications)</li>
      </ul>

      <h2>4. AI and Automated Features</h2>
      <p>
        Certain features (including our assistant, Rynn) may process prompts and context you provide to
        generate responses. We implement these features to assist with compliance-related workflows. Do not
        submit sensitive information you are not comfortable having processed in accordance with this Policy.
        Outputs are informational only and not a substitute for professional advice.
      </p>

      <h2>5. How We Share Information</h2>
      <p>We may share information with:</p>
      <ul>
        <li>
          <strong>Service providers</strong> who perform services on our behalf (hosting, email, SMS, analytics,
          payments, customer support), subject to confidentiality and data-processing terms.
        </li>
        <li>
          <strong>Legal and safety:</strong> when required by law, legal process, or to protect rights,
          safety, and security.
        </li>
        <li>
          <strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets,
          with notice where required.
        </li>
        <li>
          <strong>With your direction:</strong> such as when you share a Compliance Passport™ link or
          integrate third-party services you authorize.
        </li>
      </ul>
      <p>We do not sell your personal information as &quot;sale&quot; is defined under applicable U.S. state laws.</p>

      <h2>6. Cookies and Similar Technologies</h2>
      <p>
        We use cookies and similar technologies for session management, preferences, security, and analytics.
        You may control cookies through your browser settings; disabling cookies may affect functionality.
      </p>

      <h2>7. Data Retention</h2>
      <p>
        We retain information for as long as your account is active or as needed to provide the Services,
        comply with law, resolve disputes, and enforce agreements. Retention periods may vary by data category
        and legal requirements.
      </p>

      <h2>8. Security</h2>
      <p>
        We implement technical and organizational measures designed to protect information. No method of
        transmission or storage is completely secure; we cannot guarantee absolute security.
      </p>

      <h2>9. Your Rights and Choices</h2>
      <p>
        Depending on your location, you may have rights to access, correct, delete, or export certain personal
        information, or to object to or restrict certain processing. You may update some information in your
        account settings. To exercise other rights, contact{' '}
        <a href="mailto:support@sovrynn.co">support@sovrynn.co</a>. We will respond in accordance with applicable
        law.
      </p>

      <h2>10. Children</h2>
      <p>
        The Services are not directed to children under 16, and we do not knowingly collect their personal
        information. If you believe we have collected such information, contact us and we will take appropriate
        steps.
      </p>

      <h2>11. International Users</h2>
      <p>
        If you access the Services from outside the United States, your information may be processed in the
        United States and other countries where we or our providers operate, which may have different data
        protection laws than your country.
      </p>

      <h2>12. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post the revised Policy and update the
        effective date. Where required, we will provide additional notice or obtain consent.
      </p>

      <h2>13. SMS Messages</h2>
      <p>
        If you opt in to SMS, Twilio delivers messages from Rynn. Messages and photos sent by text are
        processed by an AI service provider to read documents and answer questions about your own account.
        We do not sell phone numbers or text messages, and we do not share SMS opt-in data with third
        parties for marketing. You can opt out at any time by replying STOP.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions about this Policy: <a href="mailto:support@sovrynn.co">support@sovrynn.co</a>
      </p>
    </LegalDocumentLayout>
  );
}
